import { Button, makeStyles } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { useClientOptions, useUser, useUserPermissions } from '@paintscout/ui-business'
import AddBoxIcon from '@material-ui/icons/AddBox'
import ArchiveIcon from '@material-ui/icons/Archive'
import ConvertIcon from '@material-ui/icons/SwapHoriz'
import ListIcon from '@material-ui/icons/List'
import ErrorIcon from '@material-ui/icons/Error'
import get from 'lodash/get'
import React, { useMemo, useState } from 'react'
import type { RenderableItem } from '@paintscout/util/builder'
import { getFeatures, getItems, getObjectLabels, getQuoteOptions, isSignatureRequired } from '@paintscout/util/builder'
import { useQuote } from '../context'
import ItemRowContent from './ItemRowContent'
import OutlinedItemTable from './OutlinedItemTable'
import type { OutlinedItemTableProps } from './OutlinedItemTable'

export interface PendingTableNextProps {
  selectedItems?: OutlinedItemTableProps['selectedItems']
  onItemSelection?: OutlinedItemTableProps['onItemSelection']
  onItemAction: (action: string, section: 'pending', item: RenderableItem) => void
  collapsed?: boolean
}

const useStyles = makeStyles(
  (theme: Theme) => ({
    root: {
      marginTop: theme.spacing(3)
    },
    heading: {
      marginBottom: theme.spacing(1.5)
    },
    approveButton: {
      color: theme.palette.warning.main
    }
  }),
  { name: 'PendingTableNext' }
)

/**
 * The "Pending Additional Work" section for the new (estimateNext) pipeline, ported from
 * `PendingTable` onto the shared outlined primitives. The heading (+ approval button) sits above
 * the card.
 */
export default function PendingTableNext({
  collapsed,
  selectedItems,
  onItemSelection,
  onItemAction
}: PendingTableNextProps) {
  const classes = useStyles()
  const { quote, tableView, isEditable, isNoteEditable, onAccept, onReorder, onAddItem, canAddAdditionalWork } =
    useQuote()
  const { options } = useClientOptions()
  const { isCrew, isOfficeUser } = useUser()
  const { permissionToWriteAllDocuments } = useUserPermissions()
  const features = getFeatures({ options })
  const canConvertToLineItem = get(features, 'quotes.convertToLineItem')
  const objectLabels = getObjectLabels({ options, invoice: quote.is_invoice })
  const { addIndex, hideAllPrices } = getQuoteOptions({ quote, options })
  const [responding, setResponding] = useState(false)
  const signatureRequired = isSignatureRequired({ options, quote, path: 'requireSignatureAdditionalWork' })

  const items: RenderableItem[] = useMemo(
    () =>
      getItems({
        quote,
        section: 'pending',
        view: tableView,
        consumer: 'customer',
        options,
        overrideOptions: { showPrices: true }
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [quote, tableView, options]
  )

  if (items.length === 0) {
    return null
  }
  if (collapsed && !isEditable) {
    return null
  }

  function handleReorder({ items, addIndex }) {
    onReorder?.({ items, section: 'pending', view: tableView, addIndex })
  }

  const useCheckboxes = !!(isEditable || isCrew || isOfficeUser)

  return (
    <div className={classes.root}>
      <OutlinedItemTable
        sectionTitle={`Pending ${objectLabels.additionalWork.plural}`}
        sectionAction={
          <Button
            id="allow-propagation"
            onClick={async () => {
              if (!signatureRequired) {
                setResponding(true)
              }
              await onAccept?.(true, quote)
              setResponding(false)
            }}
            loading={responding}
            variant={'text'}
            icon={ErrorIcon}
            classes={{ root: classes.approveButton }}
          >
            {objectLabels.option.value} Approval Required
          </Button>
        }
        collapsed={collapsed}
        headerVariant="light"
        title={'Item'}
        items={items}
        selectedItems={selectedItems}
        showAddButton={permissionToWriteAllDocuments && isEditable && (!quote.is_invoice || !quote.parent_quote)}
        addButtonLabel={'Add Area or Line Item'}
        checkboxes={useCheckboxes}
        addIndex={addIndex.additional}
        section={'pending'}
        onReorder={handleReorder}
        onItemSelection={onItemSelection}
        onItemClick={
          (!quote.is_invoice || permissionToWriteAllDocuments || isEditable) && (isEditable || isNoteEditable)
            ? (_ev, item) => onItemAction('edit', 'pending', item as RenderableItem)
            : undefined
        }
        onAddClick={
          canAddAdditionalWork ? (_ev, insertAfterKey, type) => onAddItem('pending', insertAfterKey, type) : undefined
        }
        renderRowColumns={(item: RenderableItem) => {
          const actions = []
          if (!quote.is_invoice && !isCrew && !isOfficeUser) {
            actions.push({ key: 'move-to-bid', label: `Move to ${objectLabels.quote.value}`, icon: AddBoxIcon })
          }
          if (quote.is_invoice || isCrew || isOfficeUser || permissionToWriteAllDocuments) {
            actions.push({ key: 'make-option', label: `Make ${objectLabels.option.value}`, icon: AddBoxIcon })
          }
          if (permissionToWriteAllDocuments || isCrew || isOfficeUser || isEditable) {
            actions.push({ key: 'archive', label: 'Archive', icon: ArchiveIcon })
          }
          if (item.type === 'group' && (permissionToWriteAllDocuments || isEditable)) {
            actions.push({ key: 'ungroup', label: 'Ungroup', icon: ListIcon })
          }
          if (canConvertToLineItem && item.type !== 'line_item') {
            actions.push({ key: 'convert', label: 'Convert to Line Item', icon: ConvertIcon })
          }

          return (
            <ItemRowContent
              item={item}
              section={'pending'}
              isEditable={isEditable || isCrew || isOfficeUser}
              showAmount={!hideAllPrices}
              actions={useCheckboxes ? actions : undefined}
              onAction={(action, it) => onItemAction(action, 'pending', it)}
            />
          )
        }}
      />
    </div>
  )
}
